import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { eventsAPI } from './services/api';
import ParticipantsDialog from './ParticipantsDialog';
import DescriptionOrg from './DescriptionOrg';
import { formatDate, formatTime } from './utils/dateUtils';

function PastEventCard({ event, currentUser, onOpenGallery, onOrganizerClick, hideEventLink = false }) {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [loadedEvent, setLoadedEvent] = useState(null);
  const [showParticipantsDialog, setShowParticipantsDialog] = useState(false);
  const [showMapDialog, setShowMapDialog] = useState(false);

  const gatheringId = event.anyGatheringId;
  const dateTime = event.anyGatheringDateTime || event.dateTime;

  const loadAndOpen = async () => {
    if (!gatheringId) return;
    if (loadedEvent) {
      setShowParticipantsDialog(true);
      return;
    }
    setLoading(true);
    try {
      const full = await eventsAPI.getById(gatheringId);
      setLoadedEvent(full);
      setShowParticipantsDialog(true);
    } catch (err) {
      console.error('Error loading gathering:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="gathering-card past-event">
      <div className="gathering-header">
        <div className="gathering-title">
          <span className="past-indicator">🕒</span>
          <strong>{event.name}</strong>
        </div>
        <span className="status-badge ended-badge">{t('gatheringsSidebar.badges.ended')}</span>
        {!hideEventLink && (
          <a
            className="event-page-btn"
            href={`/events/${event.id}`}
            target="_blank"
            rel="noopener noreferrer"
            title={t('communityEventCard.viewEventPage')}
          >
            ↗
          </a>
        )}
      </div>
      {event.organizer && (
        <DescriptionOrg
          creator={event.organizer}
          creatorDescription={event.description}
          organizer={event.gatheringOrganizer || null}
          organizerDescription={event.gatheringDescription || null}
          onUserClick={onOrganizerClick}
        />
      )}
      {event.hashtags?.length > 0 && (
        <div className="gathering-hashtags">
          {event.hashtags.map(tag => (
            <span key={tag} className="gathering-hashtag">#{tag}</span>
          ))}
        </div>
      )}
      <div className="gathering-details">
        {event.address && (
          <p className="gathering-address clickable-address" onClick={() => setShowMapDialog(true)}>
            📍 {event.address}
          </p>
        )}
        {dateTime && (
          <p className="gathering-date">
            📅 {formatDate(dateTime, i18n.language)}
            {formatTime(dateTime, i18n.language) && ` ${t('gatheringsSidebar.at')} ${formatTime(dateTime, i18n.language)}`}
          </p>
        )}
        {gatheringId && (
          <p
            className="gathering-participants clickable"
            onClick={loadAndOpen}
            title={t('gatheringsSidebar.clickToSeeParticipants')}
          >
            {loading ? '...' : `👥 ${event.gatheringParticipantCount || '?'} ${t('gatheringsSidebar.people')}`}
          </p>
        )}
        {onOpenGallery && (
          <p className="gallery-link" onClick={() => onOpenGallery(event)}>
            📷 {t('gatheringsSidebar.gallery')}
          </p>
        )}
      </div>
      <div className="gathering-actions">
        <button className="react-btn" onClick={loadAndOpen} disabled={loading}>
          {loading ? '...' : t('gatheringsSidebar.buttons.react')}
        </button>
      </div>

      {showParticipantsDialog && loadedEvent && (
        <ParticipantsDialog
          event={loadedEvent}
          title={event.name}
          currentUser={currentUser}
          isPast={true}
          onClose={() => setShowParticipantsDialog(false)}
          onUserClick={onOrganizerClick}
        />
      )}

      {showMapDialog && createPortal(
        <div className="map-dialog-overlay" onClick={() => setShowMapDialog(false)}>
          <div className="map-dialog" onClick={e => e.stopPropagation()}>
            <div className="map-dialog-header">
              <span>📍 {event.address}</span>
              <button className="map-dialog-close" onClick={() => setShowMapDialog(false)}>×</button>
            </div>
            <iframe
              title="Event Location"
              className="map-dialog-iframe"
              src={`https://maps.google.com/maps?q=${encodeURIComponent(event.address)}&output=embed`}
              allowFullScreen
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default PastEventCard;
